import React, { useState, useEffect } from 'react';
import { Grid, TextField } from '@material-ui/core';
import useDebounce from '../../hooks/useDebounce';
import { verifyDuplicateExamSessionName } from '../../services/validation/validation.service';
import ActionButtons from './ActionButtons';
import EditButton from './EditButton.component';
import AddSessionItemButton from './NewItemButton.component';
import { modifyExamSession } from '../../services/exams/sessions/sessions.service';
import { DetailsProps } from './DetailsProps.types';
import { BreadcrumbLevel } from '../DialogBreadcrumbs/DialogBreadcrumbs.types';
import { WorkMode } from '../SessionTree/SessionTree.types';
import { useLayoutContext } from '../../Context/Layout.context';
import { useStyles } from './SessionDetails.styles';
import DeleteButton from './DeleteButton.component';
import { blockIconForInput } from './BlockItemForInput';

interface SessionDetailsProps extends DetailsProps {
  isReadOnly: () => boolean;
  onDelete?: () => void;
  isSessionImported: boolean;
}

const SessionDetails: React.FC<SessionDetailsProps> = (props) => {
  const classes = useStyles();
  const [sessionName, setSessionName] = useState<string>(props.data.name);
  const [sessionType, setSessionType] = useState<string>(props.data.type);
  const [sessionMonth, setSessionMonth] = useState<string>(props.data.month);
  const [sessionYear, setSessionYear] = useState<string>(props.data.year);
  const [validSessionName, setIsValidSessionName] = useState<boolean>(true);
  const [validSessionType, setIsValidSessionType] = useState<boolean>(true);
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [validationStage, setValidationStage] = useState<number>(0);
  const [isValidExamSheetCode, setIsValidExamSheetCode] = useState<boolean>(true);

  const { openAlert } = useLayoutContext();

  const saveChanges = async () => {
    await modifyExamSession(props.data.id, sessionName, sessionType, sessionYear, sessionMonth).then(
      () => {
        openAlert('success');
        setIsValidExamSheetCode(true);
        props.setWorkMode(WorkMode.VIEW);
        props.onSave({ id: props.data.id }, false);
      },
      (error) => {
        if (error.response.status === 400) {
          setIsValidExamSheetCode(false);
        }
        openAlert('error');
      },
    );
  };

  const revert = () => {
    setSessionName(props.data.name);
    setSessionType(props.data.type);
    setSessionYear(props.data.year);
    setSessionMonth(props.data.month);
  };

  const closeHandle = () => {
    props.onClose(props.data, BreadcrumbLevel.SESSION, revert);
  };

  const debouncedSessionName = useDebounce(sessionName, 1000);
  const debouncedSessionType = useDebounce(sessionType, 1000);

  useEffect(() => {
    if (sessionName === props.data.name) {
      setIsValidSessionName(true);
    } else {
      if (debouncedSessionName.trim() === '' || sessionName.trim() === '') {
        setIsValidSessionName(false);
      } else {
        setValidationStage((prev) => prev + 1);
        verifyDuplicateExamSessionName(debouncedSessionName).then((result) => {
          setIsValidSessionName(result);
          setValidationStage((prev) => prev - 1);
        });
      }
    }
  }, [debouncedSessionName, sessionName, props.data.name]);

  useEffect(() => {
    if (sessionType === props.data.type) {
      setIsValidSessionType(true);
    } else {
      if (debouncedSessionType.trim() === '' || sessionType.trim() === '') {
        setIsValidSessionType(false);
      } else {
        setIsValidSessionType(true);
      }
    }
  }, [debouncedSessionType, sessionType, props.data.type]);

  const handleEditMode = () => {
    props.onEdit(props.data, () => {
      props.setDeleteConfirmationPromptOpen(true);
    });
  };
  useEffect(() => {
    if (
      validSessionName &&
      validSessionType &&
      sessionYear !== '' &&
      sessionMonth !== '' &&
      sessionName !== '' &&
      sessionType !== '' &&
      (sessionName !== props.data.name ||
        sessionType !== props.data.type ||
        sessionMonth !== props.data.month ||
        sessionYear !== props.data.year)
    ) {
      setIsDisabled(false);
    } else {
      setIsDisabled(true);
    }
  }, [sessionName, sessionYear, sessionMonth, sessionType, validSessionName, validSessionType, props.data]);

  const readOnly = props.isReadOnly();
  const editMode = props.workMode === WorkMode.EDIT;
  const fieldsDisabled = props.workMode === WorkMode.VIEW || readOnly || props.isSessionImported;

  return (
    <>
      {props.data && (
        <Grid container alignContent="space-between" style={{ height: '490px' }}>
          <Grid container justifyContent="flex-end" alignContent="center">
            {props.workMode !== WorkMode.EDIT ? (
              <EditButton workMode={props.workMode} handleEditMode={handleEditMode} isDisabled={props.isReadOnly()} />
            ) : (
              <DeleteButton
                workMode={props.workMode}
                handleDeleteMode={props.onDelete}
                isDisabled={props.isReadOnly()}
              />
            )}
          </Grid>
          <Grid container justifyContent="center" alignContent="center">
            <Grid container justifyContent="center" className={classes.headerRow}>
              <div className={classes.headerText}>
                {props.workMode !== WorkMode.EDIT ? 'Sesja egzaminacyjna' : 'Edycja sesji egzaminacyjnej'}
              </div>
            </Grid>
            <Grid container justifyContent="center">
              <TextField
                className={classes.formInput}
                value={sessionName}
                onChange={(e) => {
                  setSessionName(e.target.value.trimStart());
                }}
                id="Nazwa"
                label="Nazwa*"
                variant="outlined"
                disabled={props.workMode !== WorkMode.EDIT}
                error={props.workMode !== WorkMode.VIEW && !validSessionName && validSessionName !== undefined}
                inputProps={{
                  style: {
                    padding: '15.5px',
                    color: !editMode ? 'unset' : '#2C2D3E',
                  },
                }}
                InputProps={{
                  startAdornment: blockIconForInput(props.workMode !== WorkMode.EDIT),
                }}
                InputLabelProps={{
                  style: {
                    color: '#2C2D3E',
                  },
                }}
                FormHelperTextProps={{
                  style: {
                    fontSize: '11px',
                    color: '#BC0000',
                  },
                }}
                helperText={
                  props.workMode !== WorkMode.VIEW && !validSessionName && validSessionName !== undefined
                    ? 'Pole "Nazwa" musi być unikatowe dla sesji egzaminacyjnej'
                    : undefined
                }
              />
              <TextField
                className={classes.formInput}
                value={sessionType}
                onChange={(e) => {
                  setSessionType(e.target.value.trimStart());
                }}
                id="Typ*"
                label="Typ*"
                variant="outlined"
                disabled={fieldsDisabled}
                error={props.workMode !== WorkMode.VIEW && !validSessionType && validSessionType !== undefined}
                inputProps={{
                  style: {
                    padding: '15.5px',
                    color: !editMode ? 'unset' : '#2C2D3E',
                  },
                }}
                InputProps={{
                  startAdornment: blockIconForInput(fieldsDisabled),
                }}
                InputLabelProps={{
                  style: {
                    color: '#2C2D3E',
                  },
                }}
                FormHelperTextProps={{
                  style: {
                    fontSize: '11px',
                    color: '#BC0000',
                  },
                }}
                helperText={
                  props.workMode !== WorkMode.VIEW && !validSessionType && validSessionType !== undefined
                    ? 'Pole "Typ" jest wymagane'
                    : undefined
                }
              />
            </Grid>
            <Grid container justifyContent="center">
              <TextField
                className={classes.formInputNarrow}
                value={sessionYear}
                onChange={(e) => setSessionYear(e.target.value)}
                id="Rok"
                label="Rok*"
                variant="outlined"
                disabled={fieldsDisabled}
                error={props.workMode !== WorkMode.VIEW && !isValidExamSheetCode}
                inputProps={{
                  style: {
                    padding: '15.5px',
                    color: !editMode ? 'unset' : '#2C2D3E',
                  },
                }}
                InputLabelProps={{
                  style: {
                    color: '#2C2D3E',
                  },
                }}
                InputProps={{
                  startAdornment: blockIconForInput(fieldsDisabled),
                }}
                FormHelperTextProps={{
                  style: {
                    fontSize: '11px',
                    color: '#BC0000',
                  },
                }}
                helperText={
                  props.workMode !== WorkMode.VIEW && !isValidExamSheetCode
                    ? 'Kod arkusza musi być unikatowy w systemie'
                    : undefined
                }
              />
              <TextField
                className={classes.formInputNarrow}
                value={sessionMonth}
                onChange={(e) => {
                  setSessionMonth(e.target.value);
                }}
                id="Miesiąc"
                label="Miesiąc*"
                variant="outlined"
                disabled={fieldsDisabled}
                error={props.workMode !== WorkMode.VIEW && !isValidExamSheetCode}
                InputProps={{
                  startAdornment: blockIconForInput(fieldsDisabled),
                }}
                inputProps={{
                  style: {
                    padding: '15.5px',
                    color: !editMode ? 'unset' : '#2C2D3E',
                  },
                }}
                InputLabelProps={{
                  style: { color: '#2C2D3E' },
                }}
              />
            </Grid>
          </Grid>
          <Grid container justifyContent="flex-end">
            {props.workMode !== WorkMode.VIEW && (
              <ActionButtons disable={isDisabled} saveHandle={saveChanges} closeHandle={closeHandle} />
            )}
            {props.workMode === WorkMode.VIEW && (
              <AddSessionItemButton
                caption="Dodaj egzamin"
                disabled={props.isSessionImported || readOnly}
                onClick={() => props.onAddChild(BreadcrumbLevel.EXAM, props.data.id)}
              />
            )}
          </Grid>
          {validationStage > 0 && <div data-testid="validation_in_progress" />}
        </Grid>
      )}
    </>
  );
};

export default SessionDetails;
